import React, { useEffect } from "react";
import { useAtom } from "jotai";
import { useTranslation } from "react-i18next";
import FontSize from "./FontSize";
import { PlaySound } from "~/SoundUtils";
import * as icon from "~/Icons";
import {
  currentVRMAtom,
  isListeningAtom,
  languageAtom,
  standbyModeAtom,
  T2SEngineAtom,
  volumeAtom,
} from "~/Atom";
import { getVRMName } from "~/vrm_utils/VRMUtils";
import * as db from "~/components/db/CharacterDBUtils";
import "~/css/LanguageSelector.css";
import { useProgressModal } from "~/components/hooks/useProgressModal";
import ProgressModal from "~/components/modal/ProgressModal";
import type { Character } from "shared/Schemas";

// 言語の選択肢をBCP 47形式で定義
const languages = [
  { label: "日本語", code: "ja-JP" },
  { label: "English", code: "en-US" },
  { label: "中文 (中国)", code: "zh-CN" },
  { label: "中文 (台湾)", code: "zh-TW" },
  { label: "한국어", code: "ko-KR" },
  { label: "Tiếng Việt", code: "vi-VN" },
];

const LanguageSelector: React.FC = () => {
  const [volume] = useAtom(volumeAtom);
  const [isListening] = useAtom(isListeningAtom);
  const [, setT2SEngine] = useAtom(T2SEngineAtom);
  const [language, setLanguage] = useAtom(languageAtom);
  const [currentVRM] = useAtom(currentVRMAtom);
  const [standbyMode] = useAtom(standbyModeAtom);

  const { i18n, t } = useTranslation();

  // プログレスモーダルの管理
  const {
    isProgressModalOpen,
    progressValue,
    openProgressModal,
    closeProgressModal,
    completeProcess,
  } = useProgressModal();

  /**
   * 言語変更を処理し、サウンド再生と音声エンジンの更新を行う
   */
  const handleLanguageChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    // 切り替え前の言語を記録
    const previousLanguage = language;
    const selectedLanguage = e.target.value;

    // 3つのプロセスで管理:
    // - 初期化(init)
    // - キャラクター名 取得(character)
    // - ライセンステキスト 取得(license)
    openProgressModal([
      "init_" + selectedLanguage,
      "character_" + selectedLanguage,
      "license_" + selectedLanguage,
    ]);

    setLanguage(selectedLanguage);
    PlaySound("push", volume);
    i18n.changeLanguage(selectedLanguage);
    updateT2SEngine(selectedLanguage);

    // 初期化完了を記録
    completeProcess("init_" + selectedLanguage);

    // 言語切り換え前後に日本語が含まれているか
    const isNonJapanese = previousLanguage !== "ja-JP" && selectedLanguage !== "ja-JP";

    if (isNonJapanese) {
      // 非日本語同士の切替は表示する言語は変わらないため、完了を即時記録
      completeProcess("character_" + selectedLanguage);
      completeProcess("license_" + selectedLanguage);
    } else {
      // 変更前のCVを取得
      const currentCharacter = getVRMName(currentVRM);
      const characterData = await db.selectByName(currentCharacter);

      // キャラクター名が即時一致する条件をshouldCompleteCharacterで判定
      // その他の場合はaiConcierge:characterNameUpdatedイベントで完了
      if (shouldCompleteCharacter(characterData)) {
        completeProcess("character_" + selectedLanguage);
      }

      // ライセンステキストが即時一致する条件をshouldCompleteLicenseで判定
      // その他の場合はaiConcierge:licenseTextUpdatedイベントで完了
      if (shouldCompleteLicense(previousLanguage, selectedLanguage, characterData)) {
        completeProcess("license_" + selectedLanguage);
      }
    }
  };

  /**
   * キャラクター名が即時一致する条件を判定する
   * - nameとglobal_nameが完全一致する場合は即時完了
   * @param characterData キャラクターデータ
   * @returns 満たす場合 true
   */
  const shouldCompleteCharacter = (
    characterData: Pick<Character, "name" | "global_name"> | null
  ) => {
    return characterData !== null && characterData.name === characterData.global_name;
  };

  /**
   * ライセンステキストが即時一致する条件を判定する
   * - 変更前の日本語エンジンがOpenAIの場合は即時完了
   * - 変更後の日本語エンジンがOpenAIの場合は即時完了
   * @param previousLanguage 変更前の言語コード
   * @param selectedLanguage 変更後の言語コード
   * @param characterData キャラクターデータ（TTSエンジン情報）
   * @returns 満たす場合 true
   */
  const shouldCompleteLicense = (
    previousLanguage: string,
    selectedLanguage: string,
    characterData: { tts_engine?: string } | null
  ) => {
    const isPreviousJapaneseWithOpenAI =
      previousLanguage === "ja-JP" && characterData?.tts_engine === "OpenAI";
    const isJapaneseWithOpenAI =
      selectedLanguage === "ja-JP" && characterData?.tts_engine === "OpenAI";
    return isPreviousJapaneseWithOpenAI || isJapaneseWithOpenAI;
  };

  /**
   * 選択された言語に基づいてテキスト読み上げエンジンを更新する
   */
  const updateT2SEngine = async (selectedLanguage: string) => {
    try {
      // currentVRMが空の場合は処理をスキップする
      if (!currentVRM) return;

      const currentCharacter = getVRMName(currentVRM);
      const characterData = await db.selectByName(currentCharacter);
      const defaultEngine = selectedLanguage === "ja-JP" ? "VOICEVOX" : "OpenAI";

      if (!characterData || !characterData.tts_engine) {
        setT2SEngine(defaultEngine);
        return;
      }

      setT2SEngine(selectedLanguage === "ja-JP" ? characterData.tts_engine : "OpenAI");
    } catch (error) {
      setT2SEngine(selectedLanguage === "ja-JP" ? "VOICEVOX" : "OpenAI");

      console.error("Error updating T2S engine:", error);
    }
  };

  // キャラクターまたは言語が変更されたときにT2Sエンジンを更新
  useEffect(() => {
    updateT2SEngine(language);
  }, [language, currentVRM]);

  // キャラクター名更新時にプログレスを進める
  useEffect(() => {
    if (!isProgressModalOpen) {
      // モーダルが非表示の場合、処理をスキップ
      return undefined;
    }

    /**
     * キャラクター名更新時のイベントハンドラ
     * @param e  イベントオブジェクト
     * @returns void
     */
    const onCharacterNameUpdated = (e: Event) => {
      const custom = e as CustomEvent<unknown>;
      const detail = custom?.detail as { name?: string; currentVRM?: unknown } | undefined;
      if (!detail || !detail.name) return;
      // キャラクター名取得完了を記録
      completeProcess("character_" + language);
    };

    window.addEventListener(
      "aiConcierge:characterNameUpdated",
      onCharacterNameUpdated as EventListener
    );
    return () => {
      window.removeEventListener(
        "aiConcierge:characterNameUpdated",
        onCharacterNameUpdated as EventListener
      );
    };
  }, [isProgressModalOpen, currentVRM]);

  // ライセンステキスト更新時にプログレスを進める
  useEffect(() => {
    if (!isProgressModalOpen) {
      // モーダルが非表示の場合、処理をスキップ
      return undefined;
    }

    /**
     * ライセンステキスト更新時のイベントハンドラ
     * @param e  イベントオブジェクト
     * @returns void
     */
    const onLicenseTextUpdated = (e: Event) => {
      const custom = e as CustomEvent<unknown>;
      const detail = custom?.detail as { licenseText?: string; currentVRM?: unknown } | undefined;
      if (!detail || !detail.licenseText) return;
      // ライセンステキスト取得完了を記録
      completeProcess("license_" + language);
    };

    window.addEventListener(
      "aiConcierge:licenseTextUpdated",
      onLicenseTextUpdated as EventListener
    );
    return () => {
      window.removeEventListener(
        "aiConcierge:licenseTextUpdated",
        onLicenseTextUpdated as EventListener
      );
    };
  }, [isProgressModalOpen, currentVRM]);

  return (
    <div className="header-right">
      <FontSize />
      <ProgressModal
        isOpen={isProgressModalOpen}
        progress={progressValue}
        title={t("changeLanguageTitle")}
        message={t("changeLanguageMessage")}
        onClose={closeProgressModal}
      />
      <div className={`language-selector ${isListening || standbyMode === 0 ? "" : "disable"}`}>
        <icon.MdLanguage className="icon" />
        <span className="language-fixed-label">Language</span>
        <select
          className="btn language-btn"
          name="language"
          aria-label="Language"
          onChange={handleLanguageChange}
          onMouseUp={() => PlaySound("push", volume)}
          disabled={!isListening && standbyMode !== 0}
          value={language}
        >
          {languages.map(({ code, label }) => (
            <option key={code} value={code}>
              {label}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
};

export default LanguageSelector;
